import { useSyncExternalStore } from 'react';

// Nexa as an app on the device's home screen (public/manifest.webmanifest) — the one way a
// tablet shows the console with NO browser bars, typing included: a page in full screen is
// taken out of it by Safari's keyboard (fullscreen.ts). A browser installs a web app from
// its own menu, so the console explains the way for the device (install-prompt.tsx). Chrome
// on Android may also offer its own install dialog (`beforeinstallprompt`): the console
// holds it for an «Instalar» button instead of Chrome's own banner.

/** How this device adds a web app to its home screen. */
export type InstallWay = 'ios' | 'android' | 'other';

export function installWayOf(nav: Navigator = navigator): InstallWay {
  // `standalone` exists only in iOS / iPadOS browsers — an iPad's Safari says it is a Mac.
  if ('standalone' in nav) return 'ios';
  if (/Android/i.test(nav.userAgent)) return 'android';
  return 'other';
}

interface IInstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let offer: IInstallEvent | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

// From the first module load: Chrome may fire it before any screen mounts.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    offer = e as IInstallEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    offer = null;
    emit();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The browser's own install dialog when it offers one (Chrome on Android); else `null`. */
export function useInstallOffer(): (() => Promise<void>) | null {
  const current = useSyncExternalStore(subscribe, () => offer, () => null);
  if (!current) return null;
  return async () => {
    // one use per event: the browser fires a new one if it may ask again
    offer = null;
    emit();
    try {
      await current.prompt();
    } catch {
      // the browser refused to show it: the steps stay on screen
    }
  };
}
